import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'antd';
import { FormItem, Input, Flex, Button } from 'ui';
import { injectIntl } from 'react-intl';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';

const EditSpan = styled.span`
  margin-left: auto;
  user-select: none;
`;

const EditText = styled.span`
  color: ${p => p.theme.colors.theme};
  text-decoration: underline;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: bold;
  user-select: none;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: bold;
  text-transform: uppercase;
`;

const FirstRow = styled.span`
  display: flex;
  align-items: center;
`;

const EditForm = styled.div`
  width: 100%;
`;

const StyledFormItem = styled(FormItem)`
  .ant-legacy-form-item-required::before {
    display: none;
  }
  label.ant-legacy-form-item-required::after {
    content: '*';
    display: inline-block;
    margin-left: 4px;
    color: #ff4d4f;
    font-size: 14px;
    line-height: 1;
  }
`;

const ContactPersonInfo = ({
  editModeEnabled,
  form,
  selectedPartner,
  intl,
  typeName,
  isSaving,
  savePartnerDetailsRequest,
  addPartnerDetailsRequest,
  type,
  entityId,
  renderLine,
  canEditPartner,
  setEditModeEnabled,
  isNew,
  setIsNew,
}) => {
  const checkIfFormValid = () => {
    if (
      form.getFieldValue('firstName') &&
      form.getFieldValue('lastName') &&
      form.getFieldValue('email')
    ) {
      return true;
    }
    return false;
  };
  const saveContactPersonDetails = () => {
    if (checkIfFormValid()) {
      if (isNew) {
        addPartnerDetailsRequest({
          firstName: form.getFieldValue('firstName')?.trim(),
          lastName: form.getFieldValue('lastName')?.trim(),
          email: form.getFieldValue('email')?.trim(),
          phoneNumber: form.getFieldValue('phoneNumber')?.trim(),
          partnerFunction: type,
          query: '*',
          entityId,
        });
      } else {
        savePartnerDetailsRequest({
          number: selectedPartner?.number,
          firstName: form.getFieldValue('firstName')?.trim(),
          lastName: form.getFieldValue('lastName')?.trim(),
          email: form.getFieldValue('email')?.trim(),
          phoneNumber: form.getFieldValue('phoneNumber')?.trim(),
          partnerFunction: type,
          query: '*',
          entityId,
          guid: selectedPartner?.guid,
        });
      }
    }
  };

  const handleEditModeClick = () => {
    setEditModeEnabled(s => !s);
    if (isNew) {
      setIsNew(false);
    }
  };

  const firstNameValidator = form.getFieldDecorator('firstName', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.FIRST_NAME' }) }],
    initialValue: !isNew ? selectedPartner?.firstName : '',
  });
  const lastNameValidator = form.getFieldDecorator('lastName', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.LAST_NAME' }) }],
    initialValue: !isNew ? selectedPartner?.lastName : '',
  });
  const emailValidator = form.getFieldDecorator('email', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.EMAIL' }) }],
    initialValue: !isNew ? selectedPartner?.email : '',
  });
  const phoneNumberValidator = form.getFieldDecorator('phoneNumber', {
    initialValue: !isNew ? selectedPartner?.phoneNumber : '',
  });

  const renderContactPersonEditForm = () => <>
    <FirstRow>
      <Title>
        {`${intl.formatMessage({ id: 'partners.TITLE_EDIT' })} ${typeName?.toUpperCase()}`}
      </Title>
    </FirstRow>
    <EditForm>
      <Row gutter={16}>
        <Col span={24}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.FIRST_NAME' })}>
            {firstNameValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.LAST_NAME' })}>
            {lastNameValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.EMAIL' })}>
            {emailValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.PHONE_NUMBER' })}>
            {phoneNumberValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      <Flex gap={10} marginTop={10}>
        <Button
          onClick={!isSaving && handleEditModeClick}
          disabled={isSaving}
        >
          {intl.formatMessage({ id: 'partners.CANCEL' })}
        </Button>
        <Button
          type='secondary'
          onClick={!isSaving && saveContactPersonDetails}
          disabled={isSaving || !checkIfFormValid()}
        >
          {intl.formatMessage({ id: 'partners.SAVE' })}
        </Button>
      </Flex>
    </EditForm>
  </>;

  const renderContactPersonDetails = () => <>
    <FirstRow>
      {renderLine(selectedPartner.line2, selectedPartner.line2Icon)}
      {canEditPartner && isToggleEnabled('canEditPartner') ?
        <EditSpan>
          <EditText onClick={handleEditModeClick}>
            {intl.formatMessage({ id: 'partners.TITLE_EDIT' })}
          </EditText>
        </EditSpan> :
        null}
    </FirstRow>
    {renderLine(selectedPartner.line3, selectedPartner.line3Icon)}
    {renderLine(selectedPartner.line4, selectedPartner.line4Icon)}
  </>;

  return !editModeEnabled ?
    renderContactPersonDetails() :
    renderContactPersonEditForm();
};

ContactPersonInfo.propTypes = {
  editModeEnabled: PropTypes.bool,
  form: PropTypes.object,
  selectedPartner: PropTypes.object,
  intl: PropTypes.object,
  typeName: PropTypes.string,
  isSaving: PropTypes.bool,
  savePartnerDetailsRequest: PropTypes.func,
  addPartnerDetailsRequest: PropTypes.func,
  type: PropTypes.string,
  entityId: PropTypes.string,
  renderLine: PropTypes.func,
  canEditPartner: PropTypes.bool,
  setEditModeEnabled: PropTypes.func,
};

export default injectIntl(ContactPersonInfo);
